import type {
  SocialMediaCarousel,
  SocialMediaCarouselItem
} from 'types/capi/modules';

import HighlightCarousel from '~/components/HighlightCarousel';
import Image from '~/components/Image';
import SocialMediaCard from '~/components/Social/SocialMediaCard';
import {
  InstagramIcon,
  TikTokIcon,
  TssSocialIcon,
  TswSocialIcon
} from '~/components/Social/SocialMediaIcon';
import Tracked from '~/components/Tracked';

type CarouselProps = {
  module: SocialMediaCarousel;
};

type SlideContentProps = {
  item: SocialMediaCarouselItem;
};

function BusinessIcon({ business }: { business: 'tss' | 'tsw' }) {
  switch (business) {
    case 'tss':
      return <TssSocialIcon />;
    case 'tsw':
      return <TswSocialIcon />;
  }
}

function SourceIcon({ source }: { source: 'instagram' | 'tiktok' }) {
  switch (source) {
    case 'instagram':
      return <InstagramIcon />;
    case 'tiktok':
      return <TikTokIcon />;
  }
}

function SlideContent({ item }: SlideContentProps) {
  return (
    <Tracked.ExternalLink
      href={item.source_url}
      name="SOCIAL_MEDIA_CAROUSEL"
      pagePosition="Social Media Carousel"
      target="__blank"
      variant="none"
      className="block w-full"
    >
      <div className="relative h-[450px] w-[360px] overflow-hidden">
        <Image
          src={item.image.url}
          alt={item.image.alt}
          className="size-full object-cover"
          loading="lazy"
        />
        <div className="absolute left-2 top-4">
          <BusinessIcon business={item.business} />
        </div>
        <div className="absolute bottom-2 right-4">
          <SourceIcon source={item.source} />
        </div>
      </div>
    </Tracked.ExternalLink>
  );
}

export default function SocialMediaCarousel({ module }: CarouselProps) {
  return (
    <div className="py-10 md:py-20">
      <div className="flex flex-col gap-8 pb-8 md:justify-center">
        <h1 className="text-title-xl text-center font-normal">
          Join the community
        </h1>
        <div className="flex flex-row justify-center gap-2 md:gap-6">
          <SocialMediaCard group="tss" icon={<TssSocialIcon />} />
          <SocialMediaCard group="tsw" icon={<TswSocialIcon />} />
        </div>
      </div>
      <HighlightCarousel
        slides={module.items}
        autoSlideDuration={2000}
        scaleDown={0.1}
        fade={0.4}
        getSlideKey={(slide, index) =>
          `${index}-${slide.business}-${slide.source}`
        }
        renderSlideContent={(slide) => <SlideContent item={slide} />}
      />
    </div>
  );
}
